import { takeLatest, put, call, select } from 'redux-saga/effects';
import { initialize, change } from 'redux-form';
import assign from 'lodash/assign';
import mapKeys from 'lodash/mapKeys';

import * as act from '../../actions';
import * as actNotif from '../../actions/notifications';
import * as actCategory from '../../actions/library/category';
import * as actOutletList from '../../actions/outletPages/list';
import {
  LIBRARY_CATEGORIES_PAGE_REQUEST,
  LIBRARY_CATEGORIES_LIST_REQUEST,
  LIBRARY_CATEGORIES_ON_CHANGE_OUTLET_IDS,
  LIBRARY_CATEGORIES_ON_CLICK_ROW,
  LIBRARY_CATEGORIES_ON_CLICK_CANCEL,
  LIBRARY_CATEGORIES_SUBMIT_FORM_UPDATE,
  LIBRARY_CATEGORIES_ON_CLICK_CREATE_CATEGORY,
  LIBRARY_CATEGORIES_SUBMIT_FORM_CREATE,
  LIBRARY_CATEGORIES_SUBMIT_FORM_CREATE_ALL_OUTLETS,
  LIBRARY_CATEGORIES_DELETE_CATEGORY_REQUEST,
  LIBRARY_CATEGORIES_DELETE_CATEGORY,
  LIBRARY_CATEGORIES_CANCEL_DELETE,
  LIBRARY_CATEGORIES_ON_CLICK_ASSIGN_TO_ITEM,
  LIBRARY_CATEGORIES_CANCEL_ASSIGN_TO_ITEM,
  LIBRARY_CATEGORIES_ASSIGN_TO_ITEM_GET_ITEMS_REQUEST,
  LIBRARY_CATEGORIES_SUBMIT_ASSIGN_TO_ITEM,
  LIBRARY_CATEGORIES_ASSIGN_TO_ITEM_FILL_FORM,
} from '../../actions/library/category/constants';
import { setSelectedOutletIds } from '../../actions/actionBar';
import { showPageLoadingSpinner } from '../../actions/loadingSpinner';

import * as apiOutlets from '../../api/outlets';
import * as apiCategory from '../../api/categories';
import * as apiItems from '../../api/items';
import * as apiLoyalty from '../../api/customers/loyalty';
import { getSubscriptionStatus } from '../../api/feature/getSubscriptionStatus';
import { getSelectedOutlet } from '../../utils/userProperties';
import {
  getSelectedOutletIdFromLocalStorage,
  setSelectedOutletIdToLocalStorage,
} from '../../utils/selectedOutletId';
import { assertErrorHasMessage } from '../../utils/assert';
import * as clevertapLibraryCategory from '../../clevertap/library/category';

const CATEGORY_SUCCESSFULLY_CREATED_MESSAGE = 'Category successfully created.';

let rewardCategories: any[] = [];
let pointGainCategories: any[] = [];
const featureNames = {
  loyalty: 'Loyalty',
  // eslint-disable-next-line @typescript-eslint/naming-convention
  loyalty_pro: 'Loyalty Pro',
  // eslint-disable-next-line @typescript-eslint/naming-convention
  ingredient_inventory: 'Ingredient Inventory',
};

const getCloneNotification = (state) =>
  state.outletPages.list.showCloneOutletNotification.notification;

function* fillSearchFormFromOutletIds(outletIds) {
  let assignedOutletIds: any[] = [];
  if (outletIds.length > 1) {
    assignedOutletIds = [outletIds[0]];
  } else {
    assignedOutletIds = outletIds;
  }
  const data = { outletIds: assignedOutletIds };
  yield put(initialize('search', data));
}

function* fillFormCategoryDetail(category) {
  const formCategoryDetail = {
    id: category.id,
    name: category.name,
  };
  yield put(initialize('category', formCategoryDetail));
}

function* openRightColumn() {
  yield put(actCategory.openRightColumn());
}

function* closeRightColumn() {
  yield put(actCategory.closeRightColumn());
}

function* fillFormAssignToItemFlow(request) {
  const {
    payload: { category },
  } = request;
  const form = {
    id: category.id,
    itemIds:
      category.items &&
      category.items.map((itemCategory) => {
        return itemCategory.id;
      }),
  };
  yield put(initialize('assignToItem', form));
}

function* getCloneOutletNotification() {
  const notification = yield select(getCloneNotification);
  return notification;
}

export function* getLoyalty(params) {
  yield put(act.showProgressBar());

  try {
    const response = yield call(apiLoyalty.getLoyalty, params);
    return response.data;
  } catch (error) {
    assertErrorHasMessage(error);
    // If we get an error we send Redux the appropiate action and return
    yield put(
      actNotif.error(error.message, yield getCloneOutletNotification())
    );
    return false;
  } finally {
    yield put(act.hideProgressBar());
    yield put(showPageLoadingSpinner(false));
  }
}

export function* categoriesPageRequestFlow() {
  yield put(actCategory.setListCategories([]));
  yield put(showPageLoadingSpinner(true));
  yield put(actCategory.setOutlets([]));
  yield put(act.showProgressBar());
  yield put(actCategory.setCategoryDetail(null));
  yield put(actCategory.closeRightColumn());
  try {
    const response = yield call(apiOutlets.outlets);
    yield put(actCategory.setOutlets(response.data));
    const outletIds = getSelectedOutletIdFromLocalStorage();
    yield call(fillSearchFormFromOutletIds, outletIds);
    clevertapLibraryCategory.libraryCategoryStart({
      currentOutlet: outletIds[0],
    });
    // eslint-disable-next-line @typescript-eslint/naming-convention
    yield put(actCategory.categoriesRequest({ with_items: true, page: 1 }));
    yield put(actOutletList.checkCloningOutletOnProgress());
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(
      actNotif.error(error.message, yield getCloneOutletNotification())
    );
  } finally {
    yield put(act.hideProgressBar());
  }
}

export function* categoriesRequestFlow(request) {
  const {
    payload: { params, previousCategories },
  } = request;
  try {
    yield put(act.showProgressBar());
    const loyaltyResponse = yield call(getLoyalty, null);
    const response = yield call(getSubscriptionStatus, {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      feature_names: Object.values(featureNames),
    });
    const subscriptionArray = response.data;
    const subscriptionDetail = assign(
      // @ts-expect-error ts-migrate(2448) FIXME: Block-scoped variable 'subscriptionDetail' used be... Remove this comment to see the full error message
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      subscriptionDetail,
      mapKeys(subscriptionArray, 'name')
    );
    rewardCategories = [];
    pointGainCategories = [];
    loyaltyResponse.forEach((program) => {
      if (
        program.status === 'ACTIVE' &&
        subscriptionDetail['Loyalty Pro'].is_active
      ) {
        program.rewards.forEach((reward) => {
          if (reward.reward_items) {
            reward.reward_items.forEach((rewardItem) => {
              if (rewardItem.type === 'CATEGORY') {
                rewardCategories.push(rewardItem.category_name);
              }
            });
          }
        });
        program.earning_rules.forEach((earningRule) => {
          if (earningRule.type === 'ITEM') {
            earningRule.item_rules.forEach((rule) => {
              if (rule.type === 'CATEGORY') {
                pointGainCategories.push(rule.category_name);
              }
            });
          }
        });
      }
    });
    if (params && params.keyword) {
      const resp = yield call(apiCategory.getCategoriesV1, params);
      const categories = resp.data;
      yield put(actCategory.setListCategories(categories));
      yield put(actCategory.setTotalPage(1));
      yield put(actCategory.setCurrentPage(1));
    } else {
      const resCategories = yield call(apiCategory.getCategories, {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        with_items: true,
        page: params.page,
      });
      let categories;
      if (previousCategories) {
        categories = previousCategories.concat(resCategories.data.results);
      } else {
        categories = resCategories.data.results;
      }
      yield put(actCategory.setListCategories(categories));
      yield put(actCategory.setTotalPage(resCategories.data.total_pages));
      yield put(actCategory.setCurrentPage(params.page));
    }
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(
      actNotif.error(error.message, yield getCloneOutletNotification())
    );
  } finally {
    yield put(act.hideProgressBar());
    yield put(showPageLoadingSpinner(false));
  }
}

export function* onChangeOutletIds(action) {
  yield put(actCategory.setListCategories([]));
  yield put(showPageLoadingSpinner(true));
  const {
    payload: { outletIds },
  } = action;
  const previousOutlet = yield select(getSelectedOutlet);
  clevertapLibraryCategory.libraryCategoryChangeOutlet({
    previousOutlet,
    currentOutlet: outletIds,
  });
  try {
    yield put(actCategory.setCategoryDetail(null));
    yield call(closeRightColumn);
    yield call(fillFormCategoryDetail, { id: null, name: null });
    yield put(act.showProgressBar());
    setSelectedOutletIdToLocalStorage(outletIds);
    // eslint-disable-next-line @typescript-eslint/naming-convention
    yield put(actCategory.categoriesRequest({ with_items: true, page: 1 }));
    yield put(setSelectedOutletIds(outletIds));
    yield put(change('search', 'outletIds', outletIds));
    yield put(actOutletList.checkCloningOutletOnProgress());
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(
      actNotif.error(error.message, yield getCloneOutletNotification())
    );
  } finally {
    yield put(act.hideProgressBar());
  }
}

export function* onClickRowFlow(request) {
  const {
    payload: { category },
  } = request;
  try {
    yield put(act.showProgressBar());
    yield put(actCategory.hideUpdateModal());
    const categoryDetail = yield call(
      apiCategory.getCategoryDetail,
      category.id
    );
    const categoryDetailInfo = categoryDetail.data;
    categoryDetailInfo.is_reward_category =
      rewardCategories.indexOf(categoryDetailInfo.name) > -1;
    categoryDetailInfo.is_point_gain_category =
      pointGainCategories.indexOf(categoryDetailInfo.name) > -1;
    yield put(actCategory.setCategoryDetail(categoryDetailInfo));
    yield call(fillFormCategoryDetail, categoryDetailInfo);
    yield call(openRightColumn);
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(
      actNotif.error(error.message, yield getCloneOutletNotification())
    );
  } finally {
    yield put(act.hideProgressBar());
  }
}

export function* onClickCancelFlow() {
  yield put(actCategory.setCategoryDetail(null));
  yield call(fillFormCategoryDetail, { id: null, name: null });
  yield call(closeRightColumn);
}

export function* updateCategoryFlow(request) {
  const {
    payload: { id, data, previousCategories },
  } = request;
  const keyword = data.name;
  const type = 'edit';
  let oldCategoryName = null;
  previousCategories.forEach((category) => {
    if (category.id === id) oldCategoryName = category.name;
  });
  clevertapLibraryCategory.libraryCategoryUpdateOneOutlet({
    oldName: oldCategoryName,
    newName: data.name,
  });
  try {
    yield put(act.showProgressBar());
    yield put(actCategory.isSubmittingForm(true));
    yield call(apiCategory.categoryRequestWithKeywordAndType, {
      keyword,
      type,
    });
    const response = yield call(apiCategory.updateCategory, id, data);
    const categoryData = response.data;
    const categories: any[] = [];
    let categoryExisted = false;
    previousCategories.forEach((category) => {
      if (category.id === id) {
        categories.push(categoryData);
      } else if (keyword !== category.name) {
        categories.push(category);
      } else {
        categoryExisted = true;
      }
    });
    if (categoryExisted) {
      yield put(
        actNotif.error(
          'The name of the Category must be unique',
          yield getCloneOutletNotification()
        )
      );
    } else {
      yield put(
        actNotif.success(
          'Category was successfully updated.',
          yield getCloneOutletNotification()
        )
      );
      categoryData.is_reward_category =
        rewardCategories.indexOf(categoryData.name) > -1;
      categoryData.is_point_gain_category =
        pointGainCategories.indexOf(categoryData.name) > -1;
      yield put(actCategory.setListCategories(categories));
      yield put(actCategory.setCategoryDetail(categoryData));
      yield call(fillFormCategoryDetail, categoryData);
    }
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(
      actNotif.error(error.message, yield getCloneOutletNotification())
    );
  } finally {
    yield put(act.hideProgressBar());
    yield put(actCategory.isSubmittingForm(false));
  }
}

export function* onClickCreateCategoryFlow() {
  yield put(actCategory.setCategoryDetail(null));
  yield call(fillFormCategoryDetail, { id: null, name: null });
  yield call(openRightColumn);
}

export function* createCategoryFlow(request) {
  const {
    payload: { data, previousCategories },
  } = request;
  const keyword = data.name;
  const type = 'new';
  try {
    yield put(act.showProgressBar());
    yield put(actCategory.isSubmittingForm(true));
    yield call(apiCategory.categoryRequestWithKeywordAndType, {
      keyword,
      type,
    });
    const response = yield call(apiCategory.createCategory, data);
    const categoryData = response.data;
    let categoryExist = false;
    const categories = [categoryData];
    previousCategories.forEach((category) => {
      if (keyword !== category.name) {
        categories.push(category);
      } else {
        categoryExist = true;
      }
    });
    if (categoryExist) {
      yield put(actNotif.error('The name of the Category must be unique'));
    } else {
      yield put(
        actNotif.success(
          CATEGORY_SUCCESSFULLY_CREATED_MESSAGE,
          yield getCloneOutletNotification()
        )
      );
      yield put(actCategory.setListCategories(categories));
      yield put(actCategory.setCategoryDetail(categoryData));
      yield call(fillFormCategoryDetail, categoryData);
    }
  } catch (error) {
    yield put(
      actNotif.error(
        'Failed to create category.',
        yield getCloneOutletNotification()
      )
    );
  } finally {
    yield put(act.hideProgressBar());
    yield put(actCategory.isSubmittingForm(false));
  }
}

export function* createCategoryAllOutletsFlow(request) {
  const {
    payload: { data, previousCategories },
  } = request;
  try {
    yield put(act.showProgressBar());
    yield put(actCategory.isSubmittingForm(true));
    const response = yield call(apiCategory.createCategoryAllOutlets, data);
    const categoryData = response.data;
    const categories = [categoryData];
    previousCategories.forEach((category) => {
      categories.push(category);
    });
    yield put(
      actNotif.success(
        CATEGORY_SUCCESSFULLY_CREATED_MESSAGE,
        yield getCloneOutletNotification()
      )
    );
    yield put(actCategory.setListCategories(categories));
    yield put(actCategory.setCategoryDetail(categoryData));
    yield call(fillFormCategoryDetail, categoryData);
  } catch (error) {
    yield put(
      actNotif.error(
        'Failed to save category to all outlets',
        yield getCloneOutletNotification()
      )
    );
  } finally {
    yield put(actCategory.isSubmittingForm(false));
    yield put(act.hideProgressBar());
  }
}

export function* deleteCategoryRequest(request) {
  const {
    payload: { deletedCategory },
  } = request;
  yield put(actCategory.setDeletedCategory(deletedCategory));
  yield put(actCategory.showDeleteModal());
}

export function* deleteCategoryFlow(request) {
  const {
    payload: { id, previousCategories },
  } = request;
  try {
    yield put(act.showProgressBar());
    yield call(apiCategory.deleteCategory, id);
    const categories: any[] = [];
    previousCategories.forEach((category) => {
      if (category.id !== id) {
        categories.push(category);
      }
    });
    yield put(actCategory.setListCategories(categories));
    yield put(actCategory.setCategoryDetail(null));
    yield put(actCategory.setDeletedCategory(null));
    yield put(actCategory.hideDeleteModal());
    yield call(closeRightColumn);
    yield put(
      actNotif.success(
        'Category was successfully removed.',
        yield getCloneOutletNotification()
      )
    );
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(
      actNotif.error(error.message, yield getCloneOutletNotification())
    );
  } finally {
    yield put(act.hideProgressBar());
  }
}

export function* cancelDeleteCategoryFlow() {
  yield put(actCategory.setDeletedCategory(null));
  yield put(actCategory.hideDeleteModal());
}

export function* onClickAssignToItemFlow(request) {
  const {
    payload: { category },
  } = request;
  yield put(act.showProgressBar());
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const params = {};
  try {
    const resItems = yield call(apiItems.getItems, params);
    const items = resItems.data;
    yield put(actCategory.assignToItemFillForm(category));
    yield put(actCategory.assignToItemSetCurrentPage(0));
    if (items.items) {
      yield put(actCategory.assignToItemSetTotalPage(items.total_pages));
      yield put(actCategory.assignToItemSetItems(items.items));
    }
    yield put(actCategory.showAssignToItemModal());
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(
      actNotif.error(error.message, yield getCloneOutletNotification())
    );
  } finally {
    yield put(act.hideProgressBar());
  }
}

export function* cancelAssignToItemFlow() {
  yield put(actCategory.hideAssignToItemModal());
}

export function* assignToItemGetItemsRequestFlow(request) {
  const {
    payload: { params, previousItems },
  } = request;
  try {
    yield put(act.showProgressBar());
    if (params && params.keyword) {
      // V1 API includes params with_batch to support pagination
      params.with_batch = true;
      const resItems = yield call(apiItems.getItemsV1, params);
      yield put(actCategory.assignToItemSetCurrentPage(0));
      yield put(actCategory.assignToItemSetTotalPage(1));
      const { items } = resItems.data;
      if (items) {
        yield put(actCategory.assignToItemSetItems(items));
      } else if (!resItems.data.total_count) {
        yield put(actCategory.assignToItemSetItems([]));
      }
    } else {
      const resItems = yield call(apiItems.getItems, params);
      yield put(actCategory.assignToItemSetCurrentPage(params.page));
      if (resItems.data.items) {
        yield put(
          actCategory.assignToItemSetTotalPage(resItems.data.total_pages)
        );
        const items = previousItems.concat(resItems.data.items);
        yield put(actCategory.assignToItemSetItems(items));
      }
    }
    yield put(actCategory.showAssignToItemModal());
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(
      actNotif.error(error.message, yield getCloneOutletNotification())
    );
  } finally {
    yield put(act.hideProgressBar());
  }
}

export function* submitAssignToItemFlow(request) {
  const {
    payload: { data, categoryId },
  } = request;
  try {
    yield put(act.showProgressBar());
    yield put(actCategory.hideAssignToItemModal());
    yield call(apiCategory.assignToItem, categoryId, data);
    yield put(actCategory.setCategoryDetail(null));
    yield call(closeRightColumn);
    const resCategories = yield call(apiCategory.getCategories, {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      with_items: true,
      page: 1,
    });
    const categories = resCategories.data.results;
    yield put(actCategory.setListCategories(categories));
    yield put(actCategory.setTotalPage(resCategories.data.total_pages));
    yield put(actCategory.setCurrentPage(1));
    yield put(
      actNotif.success(
        'Successfully move selected items.',
        yield getCloneOutletNotification()
      )
    );
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(
      actNotif.error(error.message, yield getCloneOutletNotification())
    );
  } finally {
    yield put(act.hideProgressBar());
  }
}

export default function* saga() {
  yield takeLatest(LIBRARY_CATEGORIES_PAGE_REQUEST, categoriesPageRequestFlow);
  yield takeLatest(LIBRARY_CATEGORIES_LIST_REQUEST, categoriesRequestFlow);
  yield takeLatest(LIBRARY_CATEGORIES_ON_CHANGE_OUTLET_IDS, onChangeOutletIds);
  yield takeLatest(LIBRARY_CATEGORIES_ON_CLICK_ROW, onClickRowFlow);
  yield takeLatest(LIBRARY_CATEGORIES_ON_CLICK_CANCEL, onClickCancelFlow);
  yield takeLatest(LIBRARY_CATEGORIES_SUBMIT_FORM_UPDATE, updateCategoryFlow);
  yield takeLatest(LIBRARY_CATEGORIES_SUBMIT_FORM_CREATE, createCategoryFlow);
  yield takeLatest(
    LIBRARY_CATEGORIES_ON_CLICK_CREATE_CATEGORY,
    onClickCreateCategoryFlow
  );
  yield takeLatest(
    LIBRARY_CATEGORIES_SUBMIT_FORM_CREATE_ALL_OUTLETS,
    createCategoryAllOutletsFlow
  );
  yield takeLatest(
    LIBRARY_CATEGORIES_DELETE_CATEGORY_REQUEST,
    deleteCategoryRequest
  );
  yield takeLatest(LIBRARY_CATEGORIES_DELETE_CATEGORY, deleteCategoryFlow);
  yield takeLatest(LIBRARY_CATEGORIES_CANCEL_DELETE, cancelDeleteCategoryFlow);
  yield takeLatest(
    LIBRARY_CATEGORIES_ON_CLICK_ASSIGN_TO_ITEM,
    onClickAssignToItemFlow
  );
  yield takeLatest(
    LIBRARY_CATEGORIES_CANCEL_ASSIGN_TO_ITEM,
    cancelAssignToItemFlow
  );
  yield takeLatest(
    LIBRARY_CATEGORIES_SUBMIT_ASSIGN_TO_ITEM,
    submitAssignToItemFlow
  );
  yield takeLatest(
    LIBRARY_CATEGORIES_ASSIGN_TO_ITEM_FILL_FORM,
    fillFormAssignToItemFlow
  );
  yield takeLatest(
    LIBRARY_CATEGORIES_ASSIGN_TO_ITEM_GET_ITEMS_REQUEST,
    assignToItemGetItemsRequestFlow
  );
}
